import { formatChipsShort } from '@polf/shared/locale'
import { getStablefordPoints } from '@polf/shared/scoring'
import { useState } from 'react'

import type { FlightPlayerScoresFragment } from '../../../__generated__/hooks'
import { holeStrokesFor } from '../../../util/strokes'
import { netTermLabel } from '../../../util/term'
import HighScoreSheet from './HighScoreSheet'
import ScoreButtons from './ScoreButtons'

/**
 * One player/team row in the score-entry grid. Header (avatar + name +
 * HCP/stroke meta), 7-cell score grid, footer (hole result + accumulated
 * round totals + optional 'Brukt av laget' best-ball pill).
 *
 * `usedByTeam` paints the gold ring + footer pill in best-ball when this
 * card's score is the team's chosen "best" on the current hole.
 *
 * `countsForSide` is the same fact INSIDE a match, where gold is spent once
 * on the close-out and the green ring already means "this side took the
 * hole" — so the contributor is marked in ink instead, on the card, while the
 * match ring sits on the side group around it (design.md 6.5b).
 */
const PlayerScoreCard = ({
  entry,
  holeNumber,
  isYou,
  showChips,
  isNet,
  partialCard = false,
  usedByTeam,
  countsForSide,
  matchStrokes,
  wonHole,
  onSetScore,
  saving,
}: {
  entry: FlightPlayerScoresFragment
  holeNumber: number
  isYou: boolean
  showChips: boolean
  /** NET_STROKES ranking — footer total shows net strokes instead of pts. */
  isNet: boolean
  /**
   * The entry holds only part of the card, so its running figure covers that
   * part rather than the round. True on a split round, where ownership moves
   * between blocks and two different spans would otherwise read as one tally.
   */
  partialCard?: boolean
  usedByTeam: boolean
  /** Best ball's counting ball, inside a match. Neutral ring, no pill. */
  countsForSide?: boolean
  /**
   * Round allowance for THIS SIDE OF A MATCH, when the hole is being played as
   * one. A match is played off the difference — the lower course handicap off
   * scratch, the higher receiving the gap — which is a different number from
   * the player's own stroke-play allowance. Showing the latter on a match hole
   * makes the standing look wrong when it is right. Undefined outside a match.
   */
  matchStrokes?: number
  /**
   * This side took the hole in front of them.
   *
   * Marked on the card rather than said in a sentence: the ring is read
   * without spending a line, and vertical space on a hole screen is the
   * scarcest thing there is. `green-700` and not gold — gold is spent once in
   * a match, on the close-out.
   */
  wonHole?: boolean
  onSetScore: (scoreId: string, gross: number) => void
  saving: boolean
}) => {
  const [overflowOpen, setOverflowOpen] = useState(false)
  const holeScore = (entry.scores ?? []).find(
    (s) => s.hole.number === holeNumber
  )
  if (!holeScore) {
    return (
      <div className="bg-paper rounded-lg p-3 shadow-sm">
        <div className="text-sm font-semibold">{entry.name}</div>
        <p className="text-xs text-ink-500 mt-2">
          Ingen score-rad funnet for dette hullet
        </p>
      </div>
    )
  }
  const { hole, score, chips } = holeScore
  const selected = score ?? null
  const acc = entry.accumulated
  // Per-hole strokes + stableford recomputed from the top-level
  // `entry.strokes` (which IS correctly resolved). The score-level
  // getters on the API return 0 for Scramble team scores because the
  // score's `.team` relation isn't loaded with polfEvent — so we
  // derive locally from the team's total and the hole's index. Same
  // algorithm as the server, from `@polf/shared/scoring`.
  // In a match the allowance is the differential, not the player's own — and
  // both the "+N" and the "no strokes" wording have to say so. The side off
  // scratch has no strokes IN THE MATCH; they still have a stroke-play
  // allowance, so calling it "ingen slag fra hcp" would be false.
  // The dot row already says "strokes received", so the "+" is redundant and
  // the qualifier is carried by context: inside a match block the number IS
  // the match allowance. See POL-79 — this drops the only cue distinguishing
  // match strokes from stroke-play strokes, which that ticket has to answer.
  const strokesLabel = 'slag'
  // In a match the card reports the MATCH: its strokes, and its net. The
  // running Stableford/to-par totals come from the API's `accumulated`, which
  // is computed off the player's OWN allowance — the individual competition —
  // so showing them here put two different competitions side by side as if
  // they were one tally. They live on the scorecard and leaderboard, where
  // they are correct and where there is room to label them (POL-79).
  const inMatch = matchStrokes != null
  const strokes = holeStrokesFor(matchStrokes, holeScore, hole.index)
  const stablefordPoints =
    selected != null ? getStablefordPoints(hole.par, selected, strokes) : 0
  const dotCount = Math.min(strokes, 4)

  return (
    <div
      className={`relative overflow-hidden bg-paper rounded-lg p-3 shadow-sm ${
        isYou ? 'bg-green-50' : ''
      } ${usedByTeam ? 'ring-2 ring-gold' : ''} ${
        countsForSide ? 'ring-2 ring-ink-700' : ''
      } ${wonHole ? 'ring-2 ring-green-700' : ''}`}
    >
      {/* "You" left-border accent per design.md §8.1 — parent uses
          overflow-hidden so the strip is clipped to the rounded corners. */}
      {isYou ? (
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-green-700"
        />
      ) : null}

      {/* One line, no avatar. "You" is already carried three ways — the green
          left strip, the tinted background and the pill — so the avatar was
          the one identity cue costing horizontal space without adding
          information. Name and pill form one group so the pill hugs the name
          instead of drifting toward the numbers; the numbers are shrink-0 and
          right-aligned, lining them up into a scannable column down the
          flight. */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
          <span
            className={`truncate text-sm font-semibold ${
              isYou ? 'text-green-800' : 'text-ink-900'
            }`}
          >
            {entry.name}
          </span>
          {isYou ? (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
              Du
            </span>
          ) : null}
        </div>
        {entry.isTeamEntry ? (
          // A team has no handicap index — two partners have two. "HCP —"
          // read as a player whose data failed to load rather than as a side.
          <span className="shrink-0 text-[11px] text-ink-500 uppercase tracking-wider">
            Laget
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-ink-500 font-mono num">
            HCP {entry.hcp ?? '—'}
          </span>
        )}
        {strokes > 0 ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px]">
            <span className="inline-flex items-center gap-0.5">
              {Array.from({ length: dotCount }).map((_, i) => (
                <span
                  // Decorative count-dots — no identity beyond index.
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-flag"
                />
              ))}
            </span>
            <span className="font-semibold text-ink-700">
              {strokes} {strokesLabel}
            </span>
          </span>
        ) : null}
      </div>

      <ScoreButtons
        par={hole.par}
        selected={selected}
        disabled={saving}
        // Toggle behaviour: tapping the currently-selected cell sends
        // 0, which the API treats as a clear (see service.updateScore
        // — `!!score ? score : null`).
        onPick={(g) => onSetScore(holeScore.id, g === selected ? 0 : g)}
        onOverflow={() => setOverflowOpen(true)}
      />

      <div className="mt-2.5 flex items-center justify-between text-xs gap-2 flex-wrap">
        {usedByTeam ? (
          <span
            className="inline-flex items-center gap-1 bg-gold/15 text-gold px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            aria-label="Brukt av laget"
          >
            🏆 Brukt av laget
          </span>
        ) : null}
        {/* No "Ingen score registrert": every button sitting inactive already
            says it, and an empty hole is the common case on the tee — the row
            it cost was pure. Unscored renders nothing at all. */}
        <div className="min-w-0">
          {selected == null ? null : (
            <span className="font-semibold text-ink-500">
              {/* The gross term is gone: the button that was just pressed
                  already says "Par" under the number, so repeating it here
                  spent a line on nothing. The NET term stays — no button can
                  show it, and it is the only place a player sees their stroke
                  land. */}
              {strokes > 0 ? (
                <span className="capitalize">
                  Netto {netTermLabel(selected - strokes, hole.par)}
                </span>
              ) : null}
              {inMatch ? null : (
                <span className={`num ${strokes > 0 ? 'ml-2' : ''}`}>
                  {strokes > 0 ? '· ' : ''}
                  {stablefordPoints} pts
                </span>
              )}
              {showChips && chips > 0 ? (
                <span className="font-mono num font-bold text-gold ml-2">
                  +{formatChipsShort(chips)}
                </span>
              ) : null}
            </span>
          )}
        </div>
        {inMatch ? null : (
          <div className="text-[11px] text-ink-500 font-mono num shrink-0">
            {/* On a split round an entry holds one block of the card, so this
                running figure covers that block and changes scope as you page
                between them. Naming it stops two different spans reading as
                one tally; the round's own total lives on the leaderboard. */}
            {partialCard ? 'Etappe' : 'Totalt'}{' '}
            {showChips ? (
              <>
                <span className="text-ink-900 font-bold">
                  {formatChipsShort(acc.chips)}
                </span>
                <span className="text-ink-300"> · </span>
              </>
            ) : null}
            {/* Primary running stat tracks the event ranking:
                - STABLEFORD: cumulative pts ("32 pts")
                - NET_STROKES: to-par over played holes ("−1" / "E" / "+5"),
                  red when under par. acc.score is sum of (netScore - par)
                  already — exactly what golfers expect to see. */}
            {isNet ? (
              (() => {
                const toPar = acc.score
                const display =
                  toPar === 0 ? 'E' : toPar > 0 ? `+${toPar}` : `${toPar}`
                const color = toPar < 0 ? 'text-flag-dark' : 'text-ink-900'
                return <span className={`font-bold ${color}`}>{display}</span>
              })()
            ) : (
              <>
                <span className="text-ink-900 font-bold">
                  {acc.stablefordPoints}
                </span>
                <span className="text-ink-500"> pts</span>
              </>
            )}
          </div>
        )}
      </div>

      <HighScoreSheet
        open={overflowOpen}
        par={hole.par}
        initial={selected != null && selected > hole.par + 3 ? selected : null}
        onClose={() => setOverflowOpen(false)}
        onSave={(g) => {
          onSetScore(holeScore.id, g)
          setOverflowOpen(false)
        }}
      />
    </div>
  )
}

export default PlayerScoreCard
